import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import {
  parseInstanceDocument,
  parseTemplateSpec,
  resolveListMaxItems,
  resolveListMinItems,
  serializeInstanceDocument,
} from '../private/templateSpecUtils';
import ProductGridItemsEditor from './ProductGridItemsEditor';
import ItemListEditor from './ItemListEditor';
import CatalogPreviewIframe, { PreviewModeToolbar } from './CatalogPreviewIframe';

const BlockInstanceFields = ({ block, values, onChange, readOnly }) => {
  const { translate } = useTranslation();
  const fields = block?.fields || [];

  const patchValues = (patch) => onChange?.({ ...(values || {}), ...patch });

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {fields.map((field) => {
        if (field.type === 'product_list') {
          return (
            <Box key={field.key}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {field.label || field.key}
              </Typography>
              <ProductGridItemsEditor
                items={values?.[field.key] || []}
                readOnly={readOnly}
                minItems={resolveListMinItems(field)}
                maxItems={resolveListMaxItems(field)}
                onChange={(items) => patchValues({ [field.key]: items })}
              />
            </Box>
          );
        }
        if (field.type === 'item_list') {
          return (
            <Box key={field.key}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {field.label || field.key}
              </Typography>
              <ItemListEditor
                items={values?.[field.key] || []}
                readOnly={readOnly}
                minItems={resolveListMinItems(field)}
                maxItems={resolveListMaxItems(field)}
                onChange={(items) => patchValues({ [field.key]: items })}
              />
            </Box>
          );
        }
        if (field.type === 'boolean') {
          return (
            <FormControlLabel
              key={field.key}
              control={
                <Switch
                  checked={!!values?.[field.key]}
                  onChange={(e) => patchValues({ [field.key]: e.target.checked })}
                  disabled={readOnly}
                />
              }
              label={field.label || field.key}
            />
          );
        }
        if (field.type === 'textarea') {
          return (
            <TextField
              key={field.key}
              label={field.label || field.key}
              value={values?.[field.key] ?? ''}
              multiline
              minRows={3}
              disabled={readOnly}
              onChange={(e) => patchValues({ [field.key]: e.target.value })}
            />
          );
        }
        return (
          <TextField
            key={field.key}
            label={field.label || field.key}
            value={values?.[field.key] ?? ''}
            type={field.type === 'number' ? 'number' : 'text'}
            disabled={readOnly}
            onChange={(e) =>
              patchValues({
                [field.key]: field.type === 'number' ? Number(e.target.value) : e.target.value,
              })
            }
          />
        );
      })}
    </Box>
  );
};

const CatalogInstanceEditor = ({
  templateJson,
  instanceJson,
  onInstanceChange,
  catalogId,
  readOnly,
  refreshKey = 0,
}) => {
  const { translate } = useTranslation();
  const [previewMode, setPreviewMode] = useState('desktop');
  const [selectedBlockId, setSelectedBlockId] = useState(null);

  const templateSpec = useMemo(() => parseTemplateSpec(templateJson), [templateJson]);
  const instance = useMemo(
    () => parseInstanceDocument(instanceJson, templateSpec),
    [instanceJson, templateSpec],
  );

  useEffect(() => {
    if (!selectedBlockId && templateSpec.blocks?.[0]) {
      setSelectedBlockId(templateSpec.blocks[0].id);
    }
  }, [templateSpec.blocks, selectedBlockId]);

  const selectedBlock = templateSpec.blocks?.find((block) => block.id === selectedBlockId);

  const updateBlockValues = (blockId, values) => {
    const next = {
      ...instance,
      blocks: { ...(instance.blocks || {}), [blockId]: values },
    };
    onInstanceChange?.(serializeInstanceDocument(next));
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '220px 1fr 320px' },
        gap: 2,
        minHeight: 640,
        height: { lg: 'calc(100vh - 280px)' },
        maxHeight: 900,
      }}
    >
      <Paper variant="outlined" sx={{ overflow: 'auto', p: 1 }}>
        <Typography variant="subtitle2" sx={{ px: 1, py: 1 }}>
          {translate('catalog.templates.blocks_title')}
        </Typography>
        {(templateSpec.blocks || []).map((block) => (
          <Box
            key={block.id}
            onClick={() => setSelectedBlockId(block.id)}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 1,
              cursor: 'pointer',
              bgcolor: block.id === selectedBlockId ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2">{block.label || block.id}</Typography>
          </Box>
        ))}
      </Paper>

      <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <PreviewModeToolbar mode={previewMode} onChange={setPreviewMode} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <CatalogPreviewIframe
            catalogId={catalogId}
            templateJson={templateJson}
            instanceJson={instanceJson}
            previewMode={previewMode}
            refreshKey={`${refreshKey}-${instanceJson?.length}-${previewMode}`}
            allowSynthetic={false}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: 'auto', p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {selectedBlock?.label || translate('catalog.editor.block_fields')}
        </Typography>
        {selectedBlock ? (
          <BlockInstanceFields
            block={selectedBlock}
            values={instance.blocks?.[selectedBlock.id] || {}}
            readOnly={readOnly}
            onChange={(values) => updateBlockValues(selectedBlock.id, values)}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {translate('catalog.editor.select_block')}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default CatalogInstanceEditor;
